'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useRoomContext } from '@livekit/components-react';
import toast from 'react-hot-toast';
import { useHostSettings } from '@/lib/HostSettingsContext';
import { useHostControl } from '@/lib/HostControlContext';
import { playToastAlertSound } from '@/lib/notificationSound';
import { publishRecordingEvent } from '@/lib/recordingEvents';
import {
  fetchRecordingStatus,
  isConfirmedFileRecording,
  normalizeRecordingUiState,
  startRecordingFromRoom,
  type RecordingUiState,
} from '@/lib/recording-status-client';
import {
  RECORDING_PREPARING_BUTTON_CANCEL,
  RECORDING_PREPARING_BUTTON_TITLE,
  RECORDING_RETRY_BUTTON_LABEL,
  RECORDING_STARTING_BUTTON_STOP,
  RECORDING_STARTING_BUTTON_TITLE,
  RECORDING_START_FAILED_BODY,
} from '@/lib/recording-ui-messages';
import {
  announceRecordingStartFailed,
  markRecordingAwaitingDismissedByUser,
} from '@/lib/recording-start-failed';

type RecordingStatus = RecordingUiState;

function toastRecordError(message: string) {
  toast.error(message, {
    duration: 12_000,
    style: { maxWidth: '420px' },
  });
}

export function InjectRecordIntoBar() {
  const room = useRoomContext();
  const { isHost } = useHostSettings();
  const { isCoHost } = useHostControl();
  const canControlRecording = isHost || isCoHost;
  const [targetNode, setTargetNode] = useState<Element | null>(null);
  const targetRef = useRef<Element | null>(null);
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatus>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStoppingLocally, setIsStoppingLocally] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPoll = useCallback(
    (roomName: string) => {
      stopPoll();
      pollRef.current = setInterval(async () => {
        try {
          const status = await fetchRecordingStatus(roomName);
          const state = normalizeRecordingUiState(status);
          setRecordingStatus(state);
          if (isConfirmedFileRecording(status)) {
            window.dispatchEvent(new CustomEvent('recording:start'));
            publishRecordingEvent(room, 'started');
            toast.success('Gravação iniciada', { icon: '🎬', duration: 4_000 });
            stopPoll();
          } else if (state === 'starting') {
            window.dispatchEvent(new CustomEvent('recording:starting'));
          } else if (state === 'preparing') {
            window.dispatchEvent(new CustomEvent('recording:preparing'));
          } else if (state === 'idle' || state === 'failed') {
            window.dispatchEvent(new CustomEvent('recording:stop'));
            stopPoll();
            if (state === 'failed') {
              setRecordingStatus('failed');
            }
          }
        } catch {
          /* ignore */
        }
      }, 2000);
    },
    [room, stopPoll],
  );

  useEffect(() => {
    const onPreparing = () => setRecordingStatus('preparing');
    const onStarting = () => setRecordingStatus('starting');
    const onStart = () => setRecordingStatus('active');
    const onStop = () => setRecordingStatus('idle');
    window.addEventListener('recording:preparing', onPreparing);
    window.addEventListener('recording:starting', onStarting);
    window.addEventListener('recording:start', onStart);
    window.addEventListener('recording:stop', onStop);
    return () => {
      window.removeEventListener('recording:preparing', onPreparing);
      window.removeEventListener('recording:starting', onStarting);
      window.removeEventListener('recording:start', onStart);
      window.removeEventListener('recording:stop', onStop);
      stopPoll();
    };
  }, [stopPoll]);

  const isRecording = recordingStatus === 'active';
  const isStarting = recordingStatus === 'starting';
  const isPreparing = recordingStatus === 'preparing';
  /** Spinner só durante submit HTTP ou parada local — preparing usa ícone âmbar. */
  const isLoading = isSubmitting || isStoppingLocally;

  useEffect(() => {
    const findTarget = (): Element | null => {
      const refBtn =
        document.querySelector('.lk-chat-toggle') ||
        document.querySelector('.lk-settings-toggle');
      if (refBtn?.parentElement) return refBtn.parentElement;
      const groups = document.querySelectorAll('.lk-control-bar .lk-button-group');
      if (groups.length > 0) return groups[groups.length - 1];
      return document.querySelector('.lk-control-bar');
    };

    const updateTarget = () => {
      const el = findTarget();
      if (el && el !== targetRef.current) {
        targetRef.current = el;
        setTargetNode(el);
      }
    };

    updateTarget();
    // Observa só a control bar (não document.body) e para quando achar o alvo.
    const bar =
      document.querySelector('.lk-control-bar') ||
      document.querySelector('[data-lk-controls]') ||
      document.body;
    let settled = false;
    const observer = new MutationObserver(() => {
      updateTarget();
      if (targetRef.current && !settled) {
        settled = true;
        observer.disconnect();
      }
    });
    observer.observe(bar, { childList: true, subtree: true });
    const interval = setInterval(() => {
      updateTarget();
      if (targetRef.current) {
        clearInterval(interval);
        observer.disconnect();
      }
    }, 2_000);
    // Timeout de segurança — não observar para sempre.
    const stopTimer = window.setTimeout(() => {
      clearInterval(interval);
      observer.disconnect();
    }, 20_000);
    return () => {
      observer.disconnect();
      clearInterval(interval);
      window.clearTimeout(stopTimer);
    };
  }, []);

  useEffect(() => {
    if (!room?.name) return;

    fetchRecordingStatus(room.name)
      .then((status) => {
        const state = normalizeRecordingUiState(status);
        if (state === 'idle' || state === 'failed' || state === 'cancelled') {
          setRecordingStatus(state === 'failed' ? 'failed' : 'idle');
          return;
        }
        setRecordingStatus(state);
        if (isConfirmedFileRecording(status)) {
          window.dispatchEvent(new CustomEvent('recording:start'));
        } else if (state === 'starting') {
          window.dispatchEvent(new CustomEvent('recording:starting'));
          startPoll(room.name);
        } else if (state === 'preparing') {
          window.dispatchEvent(new CustomEvent('recording:preparing'));
          startPoll(room.name);
        } else if (state === 'active') {
          window.dispatchEvent(new CustomEvent('recording:starting'));
          startPoll(room.name);
        }
      })
      .catch(() => setRecordingStatus('idle'));
  }, [room, room?.name, startPoll]);

  const toggleRecording = useCallback(async () => {
    if (!room?.name || isLoading) return;

    if (recordingStatus === 'idle' || recordingStatus === 'failed') {
      if (room.isE2EEEnabled) {
        toastRecordError('Gravação não é suportada em reuniões com criptografia ponta a ponta.');
        return;
      }
      if (!canControlRecording) {
        playToastAlertSound();
        toast.error('Somente anfitriões ou co-anfitriões podem iniciar uma gravação.');
        return;
      }

      setIsSubmitting(true);
      try {
        const data = await startRecordingFromRoom(room);

        if (data.httpStatus === 200 && isConfirmedFileRecording(data)) {
          setRecordingStatus('active');
          window.dispatchEvent(new CustomEvent('recording:start'));
          publishRecordingEvent(room, 'started');
          toast.success('Gravação iniciada', { icon: '🎬', duration: 4_000 });
          return;
        }

        if (data.httpStatus === 200 && (data.state === 'active' || data.state === 'starting')) {
          setRecordingStatus('starting');
          window.dispatchEvent(new CustomEvent('recording:starting'));
          publishRecordingEvent(room, 'starting');
          startPoll(room.name);
          return;
        }

        if (data.httpStatus === 202 || data.state === 'preparing') {
          setRecordingStatus('preparing');
          window.dispatchEvent(new CustomEvent('recording:preparing'));
          publishRecordingEvent(room, 'preparing', { queuePosition: data.queuePosition });
          startPoll(room.name);
          return;
        }

        if (data.httpStatus === 409 && data.isRecordingFile) {
          setRecordingStatus('active');
          window.dispatchEvent(new CustomEvent('recording:start'));
          return;
        }

        announceRecordingStartFailed(RECORDING_START_FAILED_BODY);
        setRecordingStatus('idle');
      } catch (error) {
        console.error('[InjectRecordIntoBar] Erro ao iniciar:', error);
        setRecordingStatus('idle');
        announceRecordingStartFailed(RECORDING_START_FAILED_BODY);
      } finally {
        setIsSubmitting(false);
      }
    } else if (isRecording || isStarting) {
      if (!canControlRecording) {
        playToastAlertSound();
        toast.error('Somente anfitriões ou co-anfitriões podem parar a gravação.');
        return;
      }
      if (isStarting) markRecordingAwaitingDismissedByUser();
      setIsStoppingLocally(true);

      try {
        const res = await fetch(`/api/record/stop?roomName=${encodeURIComponent(room.name)}`);
        if (res.ok || res.status === 404) {
          setRecordingStatus('idle');
          window.dispatchEvent(new CustomEvent('recording:stop'));
          publishRecordingEvent(room, 'stopped');
          toast.success('Gravação finalizada', { icon: '✅', duration: 4_000 });
        } else {
          setRecordingStatus('active');
          window.dispatchEvent(new CustomEvent('recording:start'));
          toastRecordError('Não foi possível parar a gravação.');
        }
      } catch {
        setRecordingStatus('active');
        window.dispatchEvent(new CustomEvent('recording:start'));
        toastRecordError('Falha de conexão ao parar a gravação.');
      } finally {
        setIsStoppingLocally(false);
      }
    } else if (isPreparing) {
      if (!canControlRecording) return;
      markRecordingAwaitingDismissedByUser();
      setIsSubmitting(true);
      try {
        await fetch('/api/record/cancel', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomName: room.name }),
        });
        stopPoll();
        setRecordingStatus('idle');
        window.dispatchEvent(new CustomEvent('recording:stop'));
        publishRecordingEvent(room, 'cancelled');
        toast('Solicitação de gravação cancelada', { icon: 'ℹ️' });
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [
    room,
    recordingStatus,
    isLoading,
    isRecording,
    isStarting,
    isPreparing,
    canControlRecording,
    startPoll,
    stopPoll,
  ]);

  if (!targetNode) return null;

  const buttonTitle = (() => {
    switch (recordingStatus) {
      case 'preparing':
        return canControlRecording
          ? RECORDING_PREPARING_BUTTON_CANCEL
          : RECORDING_PREPARING_BUTTON_TITLE;
      case 'starting':
        return canControlRecording ? RECORDING_STARTING_BUTTON_STOP : RECORDING_STARTING_BUTTON_TITLE;
      case 'active':
        return canControlRecording ? 'Parar Gravação (MP4)' : 'Gravação em andamento';
      case 'failed':
        return canControlRecording ? RECORDING_RETRY_BUTTON_LABEL : 'Gravação interrompida';
      default:
        if (isStoppingLocally) {
          return 'Parando gravação…';
        }
        return canControlRecording ? 'Gravar Reunião' : 'Somente anfitriões podem gravar';
    }
  })();

  return createPortal(
    <div
      className="lk-button-group"
      data-record-injected="true"
      style={{ order: 20, marginLeft: '8px', display: 'flex' }}
    >
      <button
        className={`lk-button lk-button-icon ${isRecording ? 'text-red-500 hover:bg-red-950/30' : isPreparing || isStarting ? 'text-amber-500 hover:bg-amber-950/30' : ''}`}
        onClick={toggleRecording}
        disabled={isLoading}
        title={buttonTitle}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div className="flex items-center justify-center w-6 h-6">
          {isLoading ? (
            <svg
              className="animate-spin h-4 w-4 text-current"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <div className="border-2 border-current flex items-center justify-center p-0.5 w-5 h-5 rounded-full">
              <div
                className={`w-full h-full bg-current transition-all duration-200 ${
                  isRecording ? 'rounded-[2px] scale-75' : 'rounded-full scale-100'
                } ${isPreparing || isStarting ? 'animate-pulse' : ''}`}
              />
            </div>
          )}
        </div>
      </button>
    </div>,
    targetNode,
  );
}
